// MODIFIED: this file was entirely commented out. It now holds the real session state
// for the app: the JWT, the signed-in user, and the login/logout actions.
import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

import axios, { TOKEN_STORAGE_KEY } from '../../api/axios';

export const AuthContext = createContext();

// A token is only usable while its "exp" claim is in the future
function isTokenValid(token) {
    try {
        const decoded = jwtDecode(token);
        return decoded.exp * 1000 > Date.now();
    } catch (error) {
        console.error('Invalid token:', error);
        return false;
    }
}

export default function AuthProvider({ children }) {
    const [token, setToken] = useState(null);
    const [user, setUser] = useState(null);
    // Guards the first render: without it, protected routes would redirect to /login
    // before the stored token has been read back.
    const [isLoading, setIsLoading] = useState(true);

    // Restore a previous session on page load
    useEffect(() => {
        const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);

        if (!storedToken || !isTokenValid(storedToken)) {
            localStorage.removeItem(TOKEN_STORAGE_KEY);
            setIsLoading(false);
            return;
        }

        setToken(storedToken);

        // Confirm with the server that the token is still good and get the current name/email
        axios.get('/me')
            .then((response) => setUser(response.data.user))
            .catch(() => {
                localStorage.removeItem(TOKEN_STORAGE_KEY);
                setToken(null);
            })
            .finally(() => setIsLoading(false));
    }, []);

    // Called by the Login screen once the API has handed back a token
    const login = useCallback((newToken, newUser) => {
        localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
        setToken(newToken);
        setUser(newUser);
    }, []);

    // ADDED: lets a screen push a fresh copy of the user into the session - used after
    // choosing a username and after flipping the multipack visibility switch, so the nav
    // and the gate react without a page reload.
    const updateUser = useCallback((updatedUser) => setUser(updatedUser), []);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        setToken(null);
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider value={{ token, user, isAuthenticated: Boolean(token), isLoading, login, logout, updateUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
